import React, { useEffect, useMemo, useCallback } from "react";
import { DateTime } from "luxon";
import { cents } from "byzantine";
import filters from "byzantine/src/filters";
import {
  Table,
  Checkbox,
  TableDateInput,
  TableInput,
  Chip,
  IconName,
  TableSelect,
  Toggle,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { MAX_MEMO_LENGTH } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { BulkPaymentRowType } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import {
  resolveExpediteSpeed,
  MAX_EXPEDITE_CHOICE,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments/expedite";
import * as formAdapters from "../../../../src/adapters";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import { getAchImportPermittedActions } from "../achImportDefaults";
import styles from "./BulkPaymentTable.module.scss";
import {
  SelectedRecipientCard,
  RecipientSelector,
  getRecipientError,
  getAmountError,
  getDateError,
  getTransactionTypeError,
  getExpediteError,
} from "./";

type BulkPaymentRowProps = {
  payment: BulkPaymentRowType;
  eligibilityLoading?: boolean;
  index: number;
  rowNumber?: number;
  form: any;
  recipients: API.Recipient[];
  selectedRows: number[];
  toggleRowSelection: (index: number) => void;
  handleClickEditRecipient: (
    recipientId: API.RecipientId,
    rowIndex?: number
  ) => void;
  handleClickAddRecipient: (rowIndex: number) => void;
  handleClickUnsavedRecipient?: (
    recipient: API.Recipient,
    rowIndex: number
  ) => void;
  minDate: string;
  setCustomAlertCopy: (copy: string) => void;
  // table-level direction framing, so every row signs its amount consistently
  showCollectAmounts: boolean;
  isTemplateMode?: boolean;
  isExcluded?: boolean;
  onToggleInclude?: () => void;
  isFieldEditable?: (
    field: "amount" | "description" | "recipient" | "transactionType" | "date"
  ) => boolean;
  // the template's speed ceiling on the 0/1/2 expedite_choice scale;
  maxExpediteChoice?: number;
  isImportMode?: boolean;
};

export const SPEED_COPY_CONFIG = {
  instant: {
    available: "Instant available",
    sending: "Sending instantly",
    icon: "zap" as IconName,
    // user will never be able to collect instantly, but keeping for consistency
    collecting: "Collecting instantly",
  },
  sameDay: {
    available: "Same-day available",
    sending: "Sending same day",
    icon: "clock" as IconName,
    collecting: "Collecting same day",
  },
  standard: {
    // we never display standard as available, but keeping for consistency
    available: "Standard available",
    sending: "Sending standard",
    icon: "calendar" as IconName,
    collecting: "Collecting standard",
  },
};

const BulkPaymentRow = React.memo(
  ({
    payment,
    eligibilityLoading = false,
    index,
    rowNumber,
    form,
    recipients,
    selectedRows,
    toggleRowSelection,
    handleClickEditRecipient,
    handleClickAddRecipient,
    handleClickUnsavedRecipient,
    minDate,
    setCustomAlertCopy,
    showCollectAmounts,
    isTemplateMode = false,
    isExcluded = false,
    onToggleInclude,
    isFieldEditable,
    maxExpediteChoice = 0,
    isImportMode = false,
  }: BulkPaymentRowProps) => {
    const { l10n } = useLocalization();
    const features = useUserFeatures();
    const {
      ach_payments: achPaymentsFeature,
      expedited_payments: expeditedPaymentsFeature,
      ach_payments_collect: paymentsCollectFeature,
    } = features;
    const canSendACHPayment = Boolean(achPaymentsFeature);
    const recipientError = getRecipientError(form, index);
    const amountError = getAmountError(form, index);
    const hasDateError = getDateError(form, index);
    const transactionTypeError = getTransactionTypeError(form, index);
    const expediteError = getExpediteError(form, index);
    const { chosenExpediteOption } = form.values;
    const { speedStatusWithExpediteOption, transactionType } = payment;
    const expediteCeiling = isTemplateMode
      ? maxExpediteChoice
      : MAX_EXPEDITE_CHOICE;
    const allowExpedite = expediteCeiling > 0;
    const isCollectPayment = transactionType === "collect";
    const isSendPayment = transactionType === "send";
    const actionOptions = useMemo(() => {
      if (isImportMode) {
        return getAchImportPermittedActions(features);
      }

      const options: Array<"send" | "collect"> = [];

      if (canSendACHPayment) {
        options.push("send");
      }

      if (paymentsCollectFeature) {
        options.push("collect");
      }

      return options;
    }, [isImportMode, features, canSendACHPayment, paymentsCollectFeature]);
    const speedStatus = payment.speedStatus || "";

    const getActionLabel = useCallback(
      (action: "send" | "collect") =>
        l10n.getString(action === "send" ? "send-label" : "collect-label"),
      [l10n]
    );

    // template and import rows carry the direction the backend will execute, so
    // they are never rewritten to match the user's direct-payment permissions
    useEffect(() => {
      if (isImportMode || isTemplateMode || !actionOptions.length) {
        return;
      }

      if (!actionOptions.includes(transactionType)) {
        form.setFieldValue(
          `payments.${index}.transactionType`,
          actionOptions[0]
        );
      }
    }, [
      isImportMode,
      isTemplateMode,
      actionOptions,
      form,
      index,
      transactionType,
    ]);

    const { onChange: onDateChange, ...restOfDateFieldProps } =
      formAdapters.fieldWithOnChange(form, `payments.${index}.scheduledDate`);

    // keep speedStatusWithExpediteOption in sync with the user's expedite
    // selection, this row's eligibility, and the template's ceiling
    useEffect(() => {
      if (!expeditedPaymentsFeature) return;

      const adjustedSpeed = resolveExpediteSpeed({
        chosenExpediteOption,
        speedStatus,
        maxExpediteChoice: expediteCeiling,
      });

      if (speedStatusWithExpediteOption !== adjustedSpeed) {
        form.setFieldValue(
          `payments.${index}.speedStatusWithExpediteOption`,
          adjustedSpeed
        );
      }
    }, [
      speedStatus,
      chosenExpediteOption,
      speedStatusWithExpediteOption,
      expediteCeiling,
      form,
      index,
    ]);

    const handleDateChange = (value: string) => {
      const dateTime = DateTime.fromFormat(value, "M/d/yyyy");
      const formattedDate = dateTime.toFormat("MM/dd/yyyy");
      onDateChange(formattedDate);
    };

    const getSpeedCopy = useCallback(() => {
      if (
        chosenExpediteOption &&
        speedStatusWithExpediteOption &&
        isSendPayment
      ) {
        return SPEED_COPY_CONFIG[
          speedStatusWithExpediteOption as keyof typeof SPEED_COPY_CONFIG
        ].sending;
      }

      if (
        chosenExpediteOption &&
        speedStatusWithExpediteOption &&
        isCollectPayment
      ) {
        return SPEED_COPY_CONFIG[
          speedStatusWithExpediteOption as keyof typeof SPEED_COPY_CONFIG
        ].collecting;
      }

      if (!chosenExpediteOption && speedStatus in SPEED_COPY_CONFIG) {
        if (speedStatus === "standard") {
          return isSendPayment
            ? SPEED_COPY_CONFIG[speedStatus as keyof typeof SPEED_COPY_CONFIG]
                .sending
            : SPEED_COPY_CONFIG[speedStatus as keyof typeof SPEED_COPY_CONFIG]
                .collecting;
        }

        return SPEED_COPY_CONFIG[speedStatus as keyof typeof SPEED_COPY_CONFIG]
          .available;
      }

      return "";
    }, [
      chosenExpediteOption,
      speedStatus,
      speedStatusWithExpediteOption,
      transactionType,
    ]);

    const SpeedChip = useMemo(() => {
      if (eligibilityLoading) {
        return <Chip kind="info" startIcon="loader" label="" />;
      }

      if (!speedStatus) return null;

      return (
        <Chip
          kind="info"
          label={getSpeedCopy()}
          startIcon={
            speedStatusWithExpediteOption
              ? SPEED_COPY_CONFIG[
                  speedStatusWithExpediteOption as keyof typeof SPEED_COPY_CONFIG
                ].icon
              : SPEED_COPY_CONFIG[speedStatus as keyof typeof SPEED_COPY_CONFIG]
                  .icon
          }
        />
      );
    }, [eligibilityLoading, speedStatus, getSpeedCopy]);

    const customDisplayValue = useMemo(() => {
      if (!showCollectAmounts) {
        return undefined;
      }

      const value = filters.currency(cents(payment.amount));

      if (isCollectPayment) {
        return `+${value}`;
      }

      if (isSendPayment) {
        return `-${value}`;
      }

      return undefined;
    }, [showCollectAmounts, isCollectPayment, isSendPayment, payment.amount]);

    const amountInputProps = formAdapters.useAmountInput(
      form,
      `payments.${index}.amount`,
      customDisplayValue
    );

    // in template mode: determine which fields are editable
    const canEditAmount = isFieldEditable ? isFieldEditable("amount") : true;
    const canEditDescription = isFieldEditable
      ? isFieldEditable("description")
      : true;

    // in template mode when expedite not allowed, show "Sending standard"
    const templateSpeedDisplay = useMemo(() => {
      if (!isTemplateMode || !expeditedPaymentsFeature) return null;
      if (!allowExpedite) {
        return (
          <Chip
            kind="info"
            label={
              isSendPayment
                ? SPEED_COPY_CONFIG.standard.sending
                : SPEED_COPY_CONFIG.standard.collecting
            }
            startIcon={SPEED_COPY_CONFIG.standard.icon}
          />
        );
      }
      return null;
    }, [
      isTemplateMode,
      expeditedPaymentsFeature,
      allowExpedite,
      isSendPayment,
    ]);

    const getActionDropdown = useMemo(() => {
      if (isTemplateMode) {
        return (
          <span className={styles.capitalize}>
            {getActionLabel(transactionType)}
          </span>
        );
      }
      if (actionOptions.length <= 1) {
        const labelClassName = transactionTypeError
          ? `${styles.capitalize} fontColor--error`
          : styles.capitalize;
        return (
          <span className={labelClassName}>
            {getActionLabel(transactionType)}
          </span>
        );
      }
      return (
        <TableSelect
          label={l10n.getString("label-action")}
          hasError={Boolean(transactionTypeError)}
          {...formAdapters.select(form, `payments.${index}.transactionType`)}
        >
          {actionOptions.map((action) => (
            <TableSelect.Item key={action} value={action}>
              {getActionLabel(action)}
            </TableSelect.Item>
          ))}
        </TableSelect>
      );
    }, [
      isTemplateMode,
      actionOptions,
      transactionType,
      transactionTypeError,
      getActionLabel,
      l10n,
      form,
      index,
    ]);

    const dimmedStyle =
      isTemplateMode && isExcluded
        ? { opacity: 0.4, pointerEvents: "none" as const }
        : undefined;

    return (
      <Table.Row key={`table-row__${index}`}>
        {rowNumber != null && (
          <Table.Cell>
            <span className={styles.rowNumber}>{rowNumber}</span>
          </Table.Cell>
        )}
        <Table.Cell>
          {isTemplateMode ? (
            <Toggle
              isActive={!isExcluded}
              onChange={() => onToggleInclude?.()}
            />
          ) : (
            <Checkbox
              onChange={() => toggleRowSelection(index)}
              checked={selectedRows.includes(index)}
            />
          )}
        </Table.Cell>
        <Table.Cell>
          <div style={dimmedStyle}>
            <TableDateInput
              altInput
              label={l10n.getString("label-scheduled-date")}
              placeholder=""
              minDate={minDate}
              dateFormat={"m/d/Y"}
              altFormat={"m/d/Y"}
              {...{ field: "date" }}
              useIsoOnChange={false}
              onChange={handleDateChange}
              hasError={Boolean(hasDateError)}
              {...restOfDateFieldProps}
            />
          </div>
        </Table.Cell>
        <Table.Cell>
          <div style={dimmedStyle}>
            {payment.recipient?.id ? (
              <SelectedRecipientCard
                recipientId={payment.recipient?.id}
                recipient={payment.recipient}
                rowIndex={index}
                form={form}
                recipients={recipients}
                handleClickEditRecipient={
                  isTemplateMode ? undefined : handleClickEditRecipient
                }
                handleClickUnsavedRecipient={handleClickUnsavedRecipient}
                staticCard={isTemplateMode}
                error={Boolean(recipientError)}
              />
            ) : (
              <RecipientSelector
                rowIndex={index}
                recipients={recipients}
                form={form}
                handleClickAddRecipient={handleClickAddRecipient}
                handleClickEditRecipient={handleClickEditRecipient}
                setCustomAlertCopy={setCustomAlertCopy}
                error={Boolean(recipientError)}
              />
            )}
          </div>
        </Table.Cell>
        <Table.Cell>
          <div style={dimmedStyle}>{getActionDropdown}</div>
        </Table.Cell>
        {expeditedPaymentsFeature && (
          <Table.Cell>
            <div
              style={dimmedStyle}
              className={expediteError ? styles.cellError : undefined}
            >
              {templateSpeedDisplay || SpeedChip}
            </div>
          </Table.Cell>
        )}
        <Table.Cell>
          <div style={dimmedStyle}>
            {canEditDescription ? (
              <TableInput
                maxLength={MAX_MEMO_LENGTH}
                label={l10n.getString("label-enter-description")}
                placeholder={l10n.getString("label-enter-description")}
                {...formAdapters.input(form, `payments.${index}.description`)}
              />
            ) : (
              <TableInput
                label={l10n.getString("label-enter-description")}
                isDisabled={true}
                value={payment.description}
              />
            )}
          </div>
        </Table.Cell>
        <Table.Cell>
          <div style={dimmedStyle}>
            <div className={styles.rightAligned}>
              {canEditAmount ? (
                <TableInput
                  label={l10n.getString("label-amount")}
                  placeholder={l10n.getString("label-amount")}
                  {...amountInputProps}
                  hasError={Boolean(amountError)}
                />
              ) : (
                <TableInput
                  label={l10n.getString("label-amount")}
                  isDisabled={true}
                  value={
                    customDisplayValue ??
                    filters.currency(cents(payment.amount))
                  }
                />
              )}
            </div>
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }
);

export default BulkPaymentRow;
